import { useEffect, useRef } from 'react';

/**
 * Hook utilitário para registrar listeners no Socket.IO com cleanup automático.
 * Evita vazamento de memória e duplicação de eventos.
 *
 * @param {import('socket.io-client').Socket} socket A instância do socket
 * @param {Record<string, Function>} eventMap Objeto com os eventos e seus handlers
 */
export function useSocketEvents(socket, eventMap) {
  // Mantém os handlers mais recentes na ref para evitar dependências no array do useEffect
  // Isso impede que listeners sejam recriados acidentalmente caso a referência da função mude
  const handlersRef = useRef(eventMap);
  handlersRef.current = eventMap;

  useEffect(() => {
    if (!socket) return;

    const stableHandlers = {};

    Object.keys(handlersRef.current).forEach((event) => {
      // Wrapper estável que sempre chama a versão mais atualizada do handler
      stableHandlers[event] = (...args) => handlersRef.current[event](...args);
      socket.on(event, stableHandlers[event]);
    });

    return () => {
      Object.keys(stableHandlers).forEach((event) => {
        socket.off(event, stableHandlers[event]);
      });
    };
  }, [socket]); // Só executa novamente se o objeto do socket mudar
}

/**
 * Hook utilitário para escutar os eventos de ciclo de vida do Socket.IO.
 *
 * @param {import('socket.io-client').Socket} socket A instância do socket
 * @param {Object} lifecycle Objeto com callbacks opcionais
 * @param {Function} [lifecycle.onConnect] Chamado quando conecta
 * @param {Function} [lifecycle.onDisconnect] Chamado quando desconecta
 * @param {Function} [lifecycle.onError] Chamado quando há erro de conexão
 */
export function useSocketLifecycle(
  socket,
  { onConnect, onDisconnect, onError }
) {
  const lifecycleRef = useRef({ onConnect, onDisconnect, onError });
  lifecycleRef.current = { onConnect, onDisconnect, onError };

  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => lifecycleRef.current.onConnect?.(socket.id);
    const handleDisconnect = (r) => lifecycleRef.current.onDisconnect?.(r);
    const handleError = (e) => lifecycleRef.current.onError?.(e);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleError);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleError);
    };
  }, [socket]);
}

/**
 * Mostra um loader global e minimalista na parte inferior direita da tela.
 */
export const showGlobalSocketLoading = () => {
  let loader = document.getElementById('global-socket-loader');
  if (!loader) {
    loader = document.createElement('div');
    loader.id = 'global-socket-loader';
    loader.style.cssText =
      'position: fixed; bottom: 20px; right: 20px; background-color: #fff; padding: 8px 12px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); display: flex; align-items: center; gap: 8px; z-index: 9999; font-family: sans-serif; font-size: 14px; color: #333;';
    loader.innerHTML =
      '<div style="width: 16px; height: 16px; border: 2px solid #5e72e4; border-top-color: transparent; border-radius: 50%; animation: global-socket-spin 1s linear infinite;"></div> <span>Processando...</span><style>@keyframes global-socket-spin { to { transform: rotate(360deg); } }</style>';
    document.body.appendChild(loader);
  }

  const count = parseInt(loader.getAttribute('data-count') || '0', 10) + 1;
  loader.setAttribute('data-count', count.toString());
  loader.style.display = 'flex';
};

/**
 * Esconde o loader global quando a última requisição pendente termina.
 */
export const hideGlobalSocketLoading = () => {
  const loader = document.getElementById('global-socket-loader');
  if (loader) {
    const count = Math.max(
      0,
      parseInt(loader.getAttribute('data-count') || '1', 10) - 1
    );
    loader.setAttribute('data-count', count.toString());
    if (count === 0) {
      loader.style.display = 'none';
    }
  }
};

/**
 * Função utilitária para "envelopar" (wrap) o método emit do socket.
 * Sempre que um emit tiver um callback (ack), mostrará o loader automático.
 * Adiciona também um fallback de timeout para esconder o loader se o servidor não responder.
 *
 * @param {import('socket.io-client').Socket} socket A instância do socket
 * @param {number} [timeoutMs=15000] Tempo máximo em ms para aguardar a resposta
 * @returns {import('socket.io-client').Socket} A mesma instância do socket
 */
export const wrapSocketWithGlobalLoader = (socket, timeoutMs = 15000) => {
  if (!socket || socket._isPatchedForLoading) return socket;

  const originalEmit = socket.emit;
  socket.emit = function (eventName, ...args) {
    const lastArg = args[args.length - 1];

    if (typeof lastArg === 'function') {
      showGlobalSocketLoading();

      let isDone = false;

      const timeoutId = setTimeout(() => {
        if (!isDone) {
          isDone = true;
          hideGlobalSocketLoading();
        }
      }, timeoutMs);

      const newCallback = (...cbArgs) => {
        if (!isDone) {
          isDone = true;
          clearTimeout(timeoutId);
          hideGlobalSocketLoading();
          lastArg(...cbArgs);
        }
      };

      args[args.length - 1] = newCallback;
    }

    return originalEmit.apply(this, [eventName, ...args]);
  };

  socket._isPatchedForLoading = true;
  return socket;
};

/**
 * Hook para gerenciar o ingresso (join) e saída (leave) de um socket em uma sala específica.
 * Ele também garante que o socket faça o rejoin automaticamente caso ocorra uma reconexão (evento 'connect').
 *
 * @param {import('socket.io-client').Socket} socket
 * @param {string|object} roomIdentifier Nome, array de nomes ou identificador da sala (Room)
 * @param {string} [joinEventName='join_room'] Nome do evento de ingresso emitido pro backend (ex: 'joinRoom', 'subscribe')
 * @param {string} [leaveEventName='leave_room'] Nome do evento de saída emitido pro backend
 * @param {object} [extraArgs={}] Argumentos adicionais que precisam ser enviados no join
 */
export const useSocketRoom = (
  socket,
  roomIdentifier,
  joinEventName = 'join_room',
  leaveEventName = 'leave_room',
  extraArgs = {}
) => {
  const roomIdentifierRef = useRef(roomIdentifier);
  const extraArgsRef = useRef(extraArgs);

  // Mantém refs atualizadas para não precisar readicionar listeners atoa
  useEffect(() => {
    roomIdentifierRef.current = roomIdentifier;
    extraArgsRef.current = extraArgs;
  }, [roomIdentifier, extraArgs]);

  useEffect(() => {
    if (!socket || !roomIdentifier) return;

    // Função para emitir o join
    const handleJoin = () => {
      const currentRoom = roomIdentifierRef.current;

      socket.emit(joinEventName, {
        room: currentRoom,
        queueNames: extraArgsRef.current.queueNames || currentRoom,
        ...extraArgsRef.current
      });
    };

    // Caso o socket já esteja conectado no momento do mount do hook, faz o join imediatamente.
    if (socket.connected) {
      handleJoin();
    }

    // Escuta o reconnect. Se o servidor reiniciou ou a rede caiu,
    // o socket.io reconecta sozinho, mas PERDE as salas em memória (no Redis, etc).
    // Por isso manda os dados de novo.
    socket.on('connect', handleJoin);

    return () => {
      const currentRoom = roomIdentifierRef.current;
      // Ao desmontar o componente, avisa o servidor para sair da sala
      // Se o socket estiver desconectado não precisa mandar leave pois o server já destruiu a conexão
      if (socket.connected) {
        socket.emit(leaveEventName, {
          room: currentRoom,
          ...extraArgsRef.current
        });
      }

      // Remove escuta do connect para evitar vazamento de listeners
      socket.off('connect', handleJoin);
    };
  }, [socket, roomIdentifier, joinEventName, leaveEventName]);
};
